import { useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";

const ink = "#3A4A38";
const muted = "#737D71";
const terra = "#C8664D";
const f = "font-family:Arial,Helvetica,sans-serif;";

const SIGNATURE_HTML = `<table cellpadding="0" cellspacing="0" border="0" style="${f}font-size:13px;line-height:1.5;color:${ink}">
<tr><td style="border-left:3px solid ${terra};padding-left:12px">
<div style="font-family:Georgia,serif;font-size:18px;color:${ink}">JD Drummond, MSW <span style="color:${muted}">| T.S.</span></div>
<div style="color:${ink}">Counselling &amp; Consulting · Counseling et services-conseils</div>
<div style="color:${muted}">Social worker licensed in Quebec (OTSTCFQ) and Ontario (OCSWSSW)</div>
<div style="color:${muted}">Travailleuse sociale autorisée au Québec (OTSTCFQ) et en Ontario (OCSWSSW)</div>
<div style="margin-top:4px"><a href="mailto:info@jddrummond.ca" style="color:${terra};text-decoration:none">info@jddrummond.ca</a> · <a href="https://jddrummond.ca" style="color:${terra};text-decoration:none">jddrummond.ca</a></div>
</td></tr>
<tr><td style="padding-top:12px;font-size:11px;line-height:1.45;color:${muted}">
This email address is not monitored for emergencies. If you are in crisis, call or text 9-8-8 or go to your nearest emergency department.<br/>
Ce courriel n’est pas surveillé en cas d’urgence. En situation de crise, appelez ou textez le 9-8-8 ou rendez-vous à l’urgence la plus proche.<br/><br/>
Confidentiality: this message may contain confidential information intended only for the recipient. If you received it in error, please delete it and let me know.<br/>
Confidentialité : ce message peut contenir des renseignements confidentiels destinés uniquement à son destinataire. Si vous l’avez reçu par erreur, veuillez le supprimer et m’en aviser.
</td></tr></table>`;

const copyRendered = async (el) => {
  try {
    await navigator.clipboard.write([new window.ClipboardItem({
      "text/html": new Blob([SIGNATURE_HTML], { type: "text/html" }),
      "text/plain": new Blob([el.innerText], { type: "text/plain" }),
    })]);
  } catch {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges(); sel.addRange(range);
    document.execCommand("copy");
    sel.removeAllRanges();
  }
};

export const EmailSignature = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await copyRendered(ref.current);
    setCopied(true);
    toast.success(t({ en: "Signature copied. Paste it into your email signature settings.", fr: "Signature copiée. Collez-la dans les paramètres de signature de votre courriel." }));
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <section className="mt-16" data-testid="admin-email-signature">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-serif text-2xl" style={{ color: "var(--ink)" }}>{t({ en: "Your email signature", fr: "Votre signature courriel" })}</h2>
        <button onClick={copy} className="btn-accent text-sm" data-testid="admin-copy-signature">
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t({ en: "Copied", fr: "Copiée" }) : t({ en: "Copy signature", fr: "Copier la signature" })}
        </button>
      </div>
      <p className="text-sm mb-4 max-w-2xl" style={{ color: "var(--ink-2)" }}>
        {t({ en: "Click “Copy signature”, then in Namecheap Private Email open Settings → Signatures, create a new signature and paste (Ctrl/Cmd + V).", fr: "Cliquez sur « Copier la signature », puis dans Namecheap Private Email, ouvrez Paramètres → Signatures, créez une nouvelle signature et collez-la (Ctrl/Cmd + V)." })}
      </p>
      <div className="rounded-2xl border p-6 bg-white overflow-x-auto" style={{ borderColor: "var(--border-medium)" }}>
        <div ref={ref} dangerouslySetInnerHTML={{ __html: SIGNATURE_HTML }} data-testid="admin-signature-preview" />
      </div>
    </section>
  );
};
